import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import {
  computeFlowControlSegmentActive,
  computeFlowControlSegmentPreloaded,
} from './flow-control-segment-status.compute';
import { useFlowControlSegmentLiveContext } from './use-flow-control-segment-live-context';
import type { SlotSegmentAnchor } from './slot-right-section-play-icon';

/**
 * Live/simulation segment playing + preload readiness in one subscription (for rows that need both).
 */
export function useFlowControlSegmentStatus(
  segmentAnchor: SlotSegmentAnchor,
  segmentKind?: FlowControlSegmentKind,
  slotId?: string,
  options?: { activeOverride?: boolean },
): { isActive: boolean; isPreloaded: boolean } {
  const { hasAutoCue, liveRunning, isSimulation, cueState, skippedItemIds } = useFlowControlSegmentLiveContext();

  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const simulationActiveSegmentKind = useFlowControlSimulationCueStore((s) => s.activeSegmentKind);
  const simulationActiveSegmentSlotId = useFlowControlSimulationCueStore((s) => s.activeSegmentSlotId);

  const isActive = computeFlowControlSegmentActive(segmentAnchor, segmentKind, slotId, {
    override: options?.activeOverride,
    isSimulation,
    cueState,
    simulation: {
      rows: simulationRows,
      pointer: simulationPointer,
      selectedItemIds: simulationSelectedItemIds,
      activeSegmentKind: simulationActiveSegmentKind,
      activeSegmentSlotId: simulationActiveSegmentSlotId,
    },
  });

  const isPreloaded = computeFlowControlSegmentPreloaded(segmentKind, slotId, {
    hasAutoCue,
    liveRunning,
    cueState,
    skippedItemIds,
  });

  return { isActive, isPreloaded };
}
